import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getServicesManager } from '../../../../services';
import { RootState } from '../../../../store';
import { 
  startRequest,
  requestError,
  stopRequest,
  SessionRequestType,
  updateSessionMetric,
} from './data-slice';
import { SESSION_METRICS_COLUMNS } from "interfaces/table";

export type AIGenerationFunctions = {
  requestAI: (sessionId: string, aiType: SessionRequestType) => Promise<void>
}

export const useAIGenerationFunctions = (): AIGenerationFunctions => {
  const dispatch = useDispatch();
  const sessionDetails = useSelector((state: RootState) => state.sessionDetails.sessionIdToDetails);
  const processAIRequest = useCallback(async (sessionId: string, aiType: SessionRequestType) => {
    switch (aiType) {
      case SessionRequestType.SUMMARY:
        const textSummary = await getServicesManager().requestTextSummary(sessionId);
        return textSummary?.summary ?? null;
      case SessionRequestType.EMAIL:
        return await getServicesManager().requestEmail(sessionId);
      case SessionRequestType.COACHING:
        return await getServicesManager().requestCoachingSuggestion(sessionId);
      default:
        return null;
    }
  }, []);

  const getSessionMetricColumn = useCallback((aiType: SessionRequestType): SESSION_METRICS_COLUMNS | null => {
    switch (aiType) {
    case SessionRequestType.SUMMARY:
        return SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT;
      case SessionRequestType.EMAIL:
        return SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT;
      case SessionRequestType.COACHING:
        return SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT;
      default:
        return null;
    }
  }, []);

  const requestAI = useCallback(async (sessionId: string, aiType: SessionRequestType) => {
    const sessionData = sessionDetails[sessionId]?.data;
    if (!sessionData) return;
    const sessionMetric = sessionData.sales_metric;
    if (!sessionMetric) return;
    const sessionMetricColumn = getSessionMetricColumn(aiType);
    if (!sessionMetricColumn) return;

    // Check if the request is already loading
    if (sessionDetails[sessionId]?.requestStates?.[aiType]?.isLoading) return;
    
    dispatch(startRequest({ sessionId, requestType: aiType }));
    try {
      const result = await processAIRequest(sessionId, aiType)
      if (result) {
        dispatch(updateSessionMetric({ sessionId, sessionMetric: {
            [sessionMetricColumn]: result
        } }));
      } 
    } catch (error) {
      dispatch(requestError({ sessionId, requestType: aiType, error: 'Unable to generate' }));
    } finally {
      dispatch(stopRequest({ sessionId, requestType: aiType }));
    }
    return;
  }, [sessionDetails, dispatch, processAIRequest, getSessionMetricColumn]);

  return {
    requestAI,
  };
}; 